// Pure formatting helpers shared by client + server.
//
// TODA la plataforma escribe en PESOS COLOMBIANOS con la convención de Colombia:
// punto para los miles y coma para los decimales ($3.937.500 · $5,2 B). El negocio
// está en Colombia y no se manejan dólares — decisión de Charly, 2026-08-03.
//
// Antes convivían dos formatos: `formatCop` agrupaba con COMA (formato de Estados
// Unidos, $3,937,500) y solo Configuración usaba el de punto. Para un lector
// colombiano "$3,937,500" se lee mal: la coma es su separador decimal. Por eso
// `formatCop` y `formatCopDotted` ahora son lo mismo — la segunda se mantiene solo
// para no romper los imports que ya la usaban.

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Locale de Colombia. `de-DE` agrupa igual (punto miles, coma decimal) y está
 *  garantizado en todo runtime; `es-CO` depende de los datos ICU del entorno. */
const CO = 'de-DE';

/** Monto COP completo, e.g. 3937500 -> "$3.937.500". */
export function formatCop(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString(CO)}`;
}

/** Alias histórico de `formatCop`. Los dos agrupan con punto. */
export const formatCopDotted = formatCop;

/** COP compacto, e.g. 5_200_000_000 -> "$5,2 B", 178_000_000 -> "$178 M". */
export function formatCompactCop(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '−' : '';
  // El decimal va con COMA: "5.2B" se leería como cinco mil doscientos millones.
  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1).replace('.', ',')} B`;
  if (abs >= 1_000_000) return `${sign}$${Math.round(abs / 1_000_000)} M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)} K`;
  return `${sign}$${Math.round(abs)}`;
}

/** Entero agrupado, e.g. 1000 -> "1.000". Mismo criterio que los montos: en
 *  Colombia "1,000 uds" se lee como una unidad con decimales. */
export function formatNumber(n: number): string {
  return Math.round(n).toLocaleString(CO);
}

/** Signed percentage, e.g. 12.3 -> "+12.3%", -8.2 -> "-8.2%". */
export function formatDeltaPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n}%`;
}

/** Plain percentage, e.g. 9.4 -> "9.4%". */
export function formatPct(n: number): string {
  return `${n}%`;
}

/** ISO date -> short Spanish label, e.g. "2026-04-17" -> "17 abr". */
export function formatShortDate(iso: string): string {
  // ⚠️ Tolera que venga con hora. Concatenarle 'T00:00:00Z' a un ISO completo daba
  // '2026-09-01T11:19:13.366ZT00:00:00Z', o sea una fecha inválida, y la función caía en su
  // salida de emergencia: devolver el texto crudo. El síntoma era un
  // «calculado el 2026-09-01T11:19:13.366Z» en la cara del usuario — invisible a 10px, y
  // evidente apenas la explicación del motor pasó a cuerpo grande en la ficha.
  const soloFecha = iso.slice(0, 10);
  const d = new Date(soloFecha + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]}`;
}

/** ISO date range -> "2026-05-14 → 2026-05-17". */
export function formatRange(start: string, end: string): string {
  return `${start} → ${end}`;
}
